/**
 * DisplaySection.js — Section 5: Layout and counter display configuration.
 *
 * Allows setting the visual layout type and, when tokens exist,
 * configuring a counter display with style options.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React from 'react';
import { VALID_LAYOUT_TYPES, VALID_COUNTER_STYLES } from '../../../../engine/schema';

/**
 * Format layout type for display.
 * e.g. 'single-button' -> 'Single Button'
 */
function formatLayoutName(layout) {
  return layout.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function DisplaySection({ config, dispatch, errors }) {
  const tokens = config.tokens || [];
  const display = config.display || {};

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium text-gray-400">Layout</label>
        <select
          value={display.layout || ''}
          onChange={e => dispatch({ type: 'SET_DISPLAY', updates: { layout: e.target.value || undefined } })}
          className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
        >
          <option value="">Auto (default)</option>
          {VALID_LAYOUT_TYPES.map(l => (
            <option key={l} value={l}>{formatLayoutName(l)}</option>
          ))}
        </select>
      </div>

      {tokens.length > 0 && (
        <div className="p-3 bg-gray-800/30 border border-gray-700 rounded-lg">
          <h4 className="text-xs font-bold text-gray-400 uppercase mb-3">Counter Display</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-400">Token</label>
              <select
                value={display.counterDisplay?.tokenId || ''}
                onChange={e => dispatch({
                  type: 'SET_DISPLAY',
                  updates: {
                    counterDisplay: {
                      ...display.counterDisplay,
                      tokenId: e.target.value || undefined,
                    },
                  },
                })}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
              >
                <option value="">None</option>
                {tokens.map(t => (
                  <option key={t.id} value={t.id}>{t.name || t.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-400">Style</label>
              <select
                value={display.counterDisplay?.style || 'dots'}
                onChange={e => dispatch({
                  type: 'SET_DISPLAY',
                  updates: {
                    counterDisplay: {
                      ...display.counterDisplay,
                      style: e.target.value,
                    },
                  },
                })}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
              >
                {VALID_COUNTER_STYLES.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
